import { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Snackbar, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { walletApi } from '../../../services/api/walletApi';
import '../accountTheme.css';
import './UpiPaymentScreen.css';

function UpiPaymentScreen({ onBack, amount = 0, onSubmitted }) {
  const [utr, setUtr] = useState('');
  const [toast, setToast] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [upiConfig, setUpiConfig] = useState({ upiId: 'elevengames@upi', qrImageData: null });
  const utrDigits = utr.replace(/\D/g, '');
  const valid = utrDigits.length === 12;

  useEffect(() => {
    walletApi
      .upiConfig()
      .then((data) => {
        setUpiConfig({
          upiId: data.upiId || 'elevengames@upi',
          qrImageData: data.qrImageData || null,
        });
      })
      .catch(() => {})
      .finally(() => setLoadingConfig(false));
  }, []);

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(upiConfig.upiId);
      setToast('UPI ID copied');
    } catch {
      setToast('Could not copy');
    }
  };

  const handleSubmit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const result = await walletApi.submitDeposit(amount, utrDigits);
      setToast(result.message || 'Submitted! Admin will verify your UTR.');
      onSubmitted?.();
      setTimeout(() => onBack?.(), 2500);
    } catch (err) {
      setToast(err.message || 'Could not submit deposit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className="bz-page upi-page">
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
      <Box className="bz-topbar">
        <IconButton size="small" onClick={onBack} className="bz-back-btn">
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Typography className="bz-topbar__title">UPI Payment</Typography>
        <Box sx={{ width: 32 }} />
      </Box>

      <Box className="bz-scroll">
        <Box className="bz-balance-hero upi-amount-hero">
          <Typography className="bz-balance-label">Pay exactly</Typography>
          <Typography className="bz-balance-value">₹{amount.toFixed(2)}</Typography>
        </Box>

        <Typography className="bz-section-title">Scan & pay</Typography>
        <Box className="bz-card upi-qr-card">
          {loadingConfig ? (
            <Box className="upi-qr-placeholder upi-qr-placeholder--loading">
              <CircularProgress size={28} />
            </Box>
          ) : upiConfig.qrImageData ? (
            <img src={upiConfig.qrImageData} alt="UPI QR" className="upi-qr-image" />
          ) : (
            <Box className="upi-qr-placeholder">
              <Typography className="upi-qr-text">QR</Typography>
            </Box>
          )}
          <Box className="upi-id-row" onClick={copyUpi} role="button" tabIndex={0}>
            <Typography className="upi-id-label">UPI ID</Typography>
            <Typography className="upi-id-value">{upiConfig.upiId}</Typography>
            <ContentCopyRoundedIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.5)' }} />
          </Box>
        </Box>

        <Typography className="bz-section-title">Enter UTR (12 digits)</Typography>
        <Box className="bz-card">
          <Box className="bz-input-wrap">
            <input
              value={utr}
              onChange={(e) => setUtr(e.target.value.replace(/\D/g, '').slice(0, 12))}
              placeholder="000000000000"
              inputMode="numeric"
              maxLength={12}
            />
          </Box>
          <Typography className="upi-utr-hint">
            {valid ? '✓ UTR ready to submit' : `${utrDigits.length}/12 digits`}
          </Typography>
          <Typography className="upi-utr-hint" style={{ marginTop: 8 }}>
            Admin will verify this UTR and credit your wallet
          </Typography>
        </Box>

        <Box className="bz-card deposit-note">
          <Typography className="deposit-note-title">Terms and Conditions</Typography>
          <Typography className="deposit-note-line">• Minimum add amount is ₹100</Typography>
          <Typography className="deposit-note-line">• Enter correct 12-digit UTR only</Typography>
          <Typography className="deposit-note-line">• If UTR is wrong, we are not responsible for failed credit</Typography>
          <Typography className="deposit-note-line">• Repeated wrong UTR submissions may block your ID</Typography>
        </Box>
      </Box>

      <Box className="bz-bottom-bar">
        <button type="button" className="bz-primary-btn" disabled={!valid || submitting} onClick={handleSubmit}>
          {submitting ? (
            <CircularProgress size={22} sx={{ color: '#fff' }} />
          ) : valid ? (
            'Submit UTR for verification'
          ) : (
            'Enter 12-digit UTR'
          )}
        </button>
      </Box>
    </Box>
  );
}

export default UpiPaymentScreen;
